import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Users, Heart } from "lucide-react";
import NavBar from "@/components/NavBar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, formatClassDate, formatTimeRange } from "@/lib/api";

export default function WaitlistPage() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { data: cls, isLoading, error } = useQuery({
    queryKey: ["class", classId],
    queryFn: async () => (await api.get(`/classes/${classId}`)).data,
  });

  const [form, setForm] = useState({
    parent_name: "",
    parent_email: "",
    parent_phone: "",
    student_count: 1,
    note: "",
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  if (error) {
    return (
      <>
        <NavBar />
        <div className="max-w-2xl mx-auto p-12 text-center">
          <h1 className="font-heading text-3xl">Class not found</h1>
          <Link to="/" className="btn-pill btn-outline mt-6 inline-flex">Back home</Link>
        </div>
      </>
    );
  }

  if (isLoading || !cls) {
    return (
      <>
        <NavBar />
        <div className="max-w-2xl mx-auto p-12">
          <div className="h-72 rounded-2xl bg-gg-surface border border-gg-border animate-pulse" />
        </div>
      </>
    );
  }

  const submit = async () => {
    if (!form.parent_name.trim() || !form.parent_email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/waitlist", { ...form, class_id: classId, student_count: Math.max(1, Number(form.student_count) || 1) });
      toast.success("You're on the list — we'll email you the moment a spot opens.");
      setSubmitted(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join waitlist");
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <>
        <NavBar />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
          <div className="rounded-2xl border border-gg-green/40 bg-gg-green/8 p-8 sm:p-10 text-center">
            <Heart className="text-gg-green mx-auto mb-4" size={36} />
            <h1 className="font-heading text-3xl text-[var(--gg-text)]">You're on the waitlist</h1>
            <p className="mt-3 text-gg-muted">
              We'll email <strong>{form.parent_email}</strong> the moment a spot opens in
              <em> {cls.title}</em>. Spots tend to go fast, so keep an eye on your inbox.
            </p>
            <Link to="/" className="btn-pill btn-primary inline-flex mt-6">Back home</Link>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <NavBar />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <button onClick={() => navigate(-1)} className="text-sm text-gg-brown hover:text-gg-green inline-flex items-center gap-1 mb-4">
          <ArrowLeft size={14} /> Back
        </button>

        <header className="rounded-2xl border border-gg-border bg-gg-surface p-6 sm:p-8">
          <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">Class is full</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-[var(--gg-text)] mt-2">{cls.title}</h1>
          <p className="text-sm text-gg-muted mt-2">
            {cls.class_date && formatClassDate(cls.class_date)}
            {formatTimeRange(cls.start_time, cls.end_time) ? ` · ${formatTimeRange(cls.start_time, cls.end_time)}` : ""}
          </p>
          <div className="mt-4 rounded-xl bg-gg-bg border border-gg-border p-4 text-sm text-gg-muted">
            <Users size={16} className="inline text-gg-green mr-2" />
            All {cls.capacity_total} seats are taken — but families often cancel or add a spot. Join the waitlist and we'll email you the moment a seat opens.
          </div>
        </header>

        <section className="mt-6 rounded-2xl border border-gg-border bg-gg-surface p-6 sm:p-8">
          <h2 className="font-heading text-2xl text-[var(--gg-text)]">Join the waitlist</h2>
          <p className="text-sm text-gg-muted mt-1">No payment now — we'll only contact you if a seat opens.</p>

          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <Label className="sm:col-span-2 block">
              <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Parent name</span>
              <Input value={form.parent_name} onChange={set("parent_name")} className="mt-1.5" data-testid="waitlist-parent-name" />
            </Label>
            <Label className="block">
              <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Email</span>
              <Input type="email" value={form.parent_email} onChange={set("parent_email")} className="mt-1.5" data-testid="waitlist-parent-email" />
            </Label>
            <Label className="block">
              <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Phone (optional)</span>
              <Input value={form.parent_phone} onChange={set("parent_phone")} className="mt-1.5" />
            </Label>
            <Label className="block">
              <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">How many students?</span>
              <Input type="number" min="1" max="5" value={form.student_count} onChange={set("student_count")} className="mt-1.5" />
            </Label>
            <Label className="sm:col-span-2 block">
              <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Anything we should know? (optional)</span>
              <Textarea value={form.note} onChange={set("note")} rows={3} className="mt-1.5" placeholder="Allergies, scheduling notes, etc." />
            </Label>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="btn-pill btn-primary disabled:opacity-60"
              data-testid="waitlist-submit"
            >
              {busy ? "Joining…" : "Join Waitlist"} <ArrowRight size={16} />
            </button>
          </div>
        </section>
      </main>
    </>
  );
}
